// Implements: F-UIX-019
/**
 * The platform admin's totals (UIX.8, RPT.5): per organization, plus the "No organization"
 * row only when it is non-zero, and the overall total; organization level only, never a
 * team, person or mockup.
 *
 * The admin picks a range (empty for the server's default); the totals are read for the
 * applied range and the organization list names the rows. The redesign (UX-11) leads with four
 * tiles from the overall total, then the chart and the table in cards, and loads as a skeleton. Each row is its own container,
 * keyed by organization id (checklist 14). "Exportar CSV" (owner 2026-10-19) downloads the applied
 * range, never a draft the admin has not applied, through the totals' export twin.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { Card } from "../../../shared/ui/atoms/Card";
import { Skeleton } from "../../../shared/ui/atoms/Skeleton";
import { DEFAULT_RANGE, PLATFORM_TOTALS_EXPORT_NAME, downloadPlatformTotals } from "../api/platformApi";
import type { DayRange } from "../api/platformApi";
import styles from "../components/PlatformTotals.module.css";
import { RangeForm } from "../components/RangeForm";
import { TotalsBarChart } from "../components/TotalsBarChart";
import type { ChartBar } from "../components/TotalsBarChart";
import { countText } from "../components/TotalsRow";
import { TotalsTable } from "../components/TotalsTable";
import { TotalsTiles } from "../components/TotalsTiles";
import { useOrganizationList, usePlatformTotals } from "../hooks/usePlatformTotals";
import { dayLabel } from "../model/days";
import { figuresOf, namesById, platformRows, rowLabel } from "../model/platformRows";
import type { TotalsRow } from "../model/platformRows";
import { ExportCsvContainer } from "./ExportCsvContainer";
import { OrganizationRowContainer } from "./OrganizationRowContainer";

/** The chart draws each row's generations (delivered, failed and cancelled together). */
function chartBars(
  rows: readonly TotalsRow[],
  noOrganization: string,
  describe: (label: string, value: string) => string,
): ChartBar[] {
  return rows.map((row) => {
    const label = rowLabel(row, noOrganization);
    const valueText = countText(row.generations);
    return {
      key: row.key,
      label,
      value: row.generations,
      valueText,
      description: describe(label, valueText),
    };
  });
}

/** The page while the totals load: the tiles and the table as grey shapes, announced once. */
function TotalsSkeleton(): ReactElement {
  const { t } = useTranslation();
  return (
    <div className={styles.skeleton} role="status" aria-busy="true" aria-label={t("admin.totals.loading")}>
      <div className={styles.tiles}>
        {[0, 1, 2, 3].map((tile) => (
          <div key={tile} className={styles.tile}>
            <Skeleton width="half" />
            <Skeleton shape="title" width="threeQuarters" />
          </div>
        ))}
      </div>
      <Card padded>
        <div className={styles.skeletonRows}>
          {[0, 1, 2, 3, 4].map((row) => (
            <Skeleton key={row} />
          ))}
        </div>
      </Card>
    </div>
  );
}

export function PlatformTotalsContainer(): ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<DayRange>(DEFAULT_RANGE);
  const [applied, setApplied] = useState<DayRange>(DEFAULT_RANGE);
  const totals = usePlatformTotals(applied);
  const organizations = useOrganizationList();

  const names = namesById(organizations.data?.organizations ?? []);
  const view = totals.data;
  const rows = view === undefined ? [] : platformRows(view, names);

  return (
    <div className={styles.view}>
      <RangeForm
        draft={draft}
        busy={totals.isFetching}
        onChange={setDraft}
        onSubmit={() => {
          setApplied({ from: draft.from.trim(), to: draft.to.trim() });
        }}
      />
      {totals.isError ? (
        <ErrorState
          error={totals.error}
          onRetry={() => {
            void totals.refetch();
          }}
        />
      ) : view === undefined ? (
        <TotalsSkeleton />
      ) : (
        <>
          <div className={styles.periodRow}>
            <p className={styles.period}>{t("admin.totals.range", { from: dayLabel(view.from), to: dayLabel(view.to) })}</p>
            <ExportCsvContainer download={() => downloadPlatformTotals(applied)} fallbackName={PLATFORM_TOTALS_EXPORT_NAME} />
          </div>
          {rows.length === 0 ? (
            <p>{t("admin.totals.empty")}</p>
          ) : (
            <>
              <TotalsTiles
                total={figuresOf(view.total)}
                organizations={rows.filter((row) => row.kind === "organization").length}
              />
              <Card padded>
                <TotalsBarChart
                  title={t("admin.totals.chartTitle")}
                  bars={chartBars(rows, t("admin.totals.noOrganization"), (label, value) =>
                    t("admin.totals.chartItem", { label, value }),
                  )}
                />
              </Card>
              <Card>
                <TotalsTable total={figuresOf(view.total)}>
                  {rows.map((row) => (
                    <OrganizationRowContainer key={row.key} row={row} />
                  ))}
                </TotalsTable>
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}
